import { useMutation } from "@apollo/client";
import { SelectControl, TextareaControl } from "@aurem/components";
import { Button, Modal, ModalBody, ModalFooter, Toast } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { FlattenedTranslations, useI18n } from "providers/I18nProvider";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { gql, SupportedLanguages, WebApps } from "types";
import { z } from "zod";
import type { TranslationMatch } from "./parser";

const TRANSLATIONS_QUERY = gql(/* GraphQL */ `
  query TranslationsQuery(
    $language: SupportedLanguages!
    $application: WebApps!
  ) {
    translations(language: $language, application: $application)
  }
`);

const TRANSLATION_MUTATION = gql(/* GraphQL */ `
  mutation UpdateTranslationsMode($input: UpdateTranslationsInput!) {
    updateTranslations(input: $input) {
      id
      key
      value
    }
  }
`);

const createTranslationSchema = (
  t: (key: keyof FlattenedTranslations) => string,
) =>
  z.object({
    keys: z
      .array(z.string())
      .min(1, t("apps.admin.translationMode.modal.keyRequired")),
    value: z
      .string()
      .min(1, t("apps.admin.translationMode.modal.valueRequired")),
  });

type TranslationFormData = z.infer<ReturnType<typeof createTranslationSchema>>;

type EditTranslationModalProps = {
  match: TranslationMatch;
  onClose: () => void;
};

const FORM_ID = "edit-translation-modal-form";

export const EditTranslationModal = ({
  match,
  onClose,
}: EditTranslationModalProps) => {
  const { t, language } = useI18n();

  const schema = createTranslationSchema(t);
  const methods = useForm<TranslationFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      keys: match.keys,
      value: match.value,
    },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset({ keys: match.keys, value: match.value });
  }, [match, reset]);

  const [updateTranslation, { loading }] = useMutation(TRANSLATION_MUTATION);

  const onSubmit = useCallback(
    async (data: TranslationFormData) => {
      try {
        await updateTranslation({
          variables: {
            input: {
              newTranslations: data.keys.map((key) => ({
                key,
                value: data.value,
              })),
              revertedTranslations: [],
              language: language as SupportedLanguages,
              app: WebApps.Admin,
            },
          },
          refetchQueries: [
            {
              query: TRANSLATIONS_QUERY,
              variables: {
                language: language as SupportedLanguages,
                application: WebApps.Admin,
              },
            },
          ],
        });
        onClose();
      } catch {
        Toast.error({
          title: t("apps.admin.translationMode.modal.errorSaving"),
        });
      }
    },
    [updateTranslation, onClose, t, language],
  );

  const keyOptions = useMemo(
    () =>
      match.keys.map((key) => ({
        id: key,
        text: key,
      })),
    [match.keys],
  );

  return (
    <Modal
      open
      onOpenChange={onClose}
      title={t("apps.admin.translationMode.modal.title")}
      className="max-w-2xl"
    >
      <FormProvider {...methods}>
        <ModalBody>
          <form
            id={FORM_ID}
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            data-translation-exclude
            noValidate
          >
            <SelectControl
              name="keys"
              label={t("apps.admin.translationMode.modal.affectedKeys")}
              options={keyOptions}
              required
              multiple
            />

            <TextareaControl
              name="value"
              label={t("apps.admin.translationMode.modal.newValue")}
              placeholder={t(
                "apps.admin.translationMode.modal.newValuePlaceholder",
              )}
              rows={3}
              required
            />
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="outlined" onClick={onClose}>
            {t("apps.admin.translationMode.modal.cancel")}
          </Button>
          <Button type="submit" form={FORM_ID} loading={loading}>
            {t("apps.admin.translationMode.modal.saveChanges")}
          </Button>
        </ModalFooter>
      </FormProvider>
    </Modal>
  );
};
